import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, Observable, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HttpStatusCode } from '../enums';
import { AuthenticationService } from '../services/authentication.service';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private defaultErrorMessageLifeTime: number;

  constructor(private authService: AuthenticationService, private messageService: MessageService) {
    this.defaultErrorMessageLifeTime = environment.errorMessageLifeTime;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let message = 'An unexpected error occurred';
        let title = 'Error';

        // Detect WAF or CORS-based blocked requests
        if (error.status === 0 && error.error?.isTrusted) {
          // message = 'The request was blocked, possibly by the firewall or due to CORS issues.';
          // title = 'Request Blocked';
          // this.showErrorMessage(message, title);

          return throwError(
            () =>
              new HttpErrorResponse({
                ...error,
                error: message
              })
          );
        }

        switch (error.status) {
          case HttpStatusCode.Unauthorized:
            // 401 Unauthorized
            this.authService.signOut();
            return of<HttpEvent<any>>(error.error);

          case HttpStatusCode.Forbidden:
            // 403 Forbidden
            message = 'You are not authorized to perform this action.';
            title = 'Access Denied';
            break;

          case HttpStatusCode.NotFound:
            // 404 Not Found
            message = 'The requested resource was not found.';
            title = 'Not Found';
            break;

          case HttpStatusCode.BadRequest:
            // 400 Bad Request
            if (this.isBlobJson(error)) {
              return this.handleBlobErrorResponse(error);
            }
            message = this.getErrorMessage(error.error) || 'Bad request sent to the server.';
            title = 'Bad Request';
            break;

          case HttpStatusCode.InternalServerError:
            // 500 Internal Server Error
            message = 'The server encountered an internal error.';
            title = 'Server Error';
            break;

          default:
            message = this.getErrorMessage(error.error) || message;
            title = `Error (${error.status})`;
            break;
        }

        this.showErrorMessage(message, title);

        return throwError(
          () =>
            new HttpErrorResponse({
              error: message,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url
            })
        );
      })
    );
  }

  private isBlobJson(error: HttpErrorResponse): boolean {
    return error.error instanceof Blob && error.error.type === 'application/json';
  }

  private showErrorMessage(message: string, title: string = 'Error', lifeTime?: number): void {
    if (!message) return;

    this.messageService.clear(); // optional: remove if multiple error stacking is preferred
    this.messageService.add({
      severity: 'error',
      summary: title,
      detail: message,
      life: lifeTime ?? this.defaultErrorMessageLifeTime
    });
  }

  private getErrorMessage(error: any): string {
    try {
      if (!error) return 'Unknown error occurred';

      if (typeof error === 'string') return error;

      if (error.error) return typeof error.error === 'string' ? error.error : JSON.stringify(error.error);

      if (error.message) return error.message;

      const parsedError = typeof error === 'string' ? JSON.parse(error) : error;
      return parsedError.message || parsedError.error || JSON.stringify(parsedError);
    } catch {
      return 'Unable to extract error message.';
    }
  }

  private handleBlobErrorResponse(error: HttpErrorResponse): Observable<any> {
    const reader = new FileReader();

    return from(
      new Promise((resolve, reject) => {
        reader.onload = (e: ProgressEvent<FileReader>) => {
          try {
            const result = JSON.parse(e.target?.result as string);
            const message = result?.message || 'An error occurred.';
            this.showErrorMessage(message, 'Error');
            reject(
              new HttpErrorResponse({
                error: result,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url
              })
            );
          } catch {
            this.showErrorMessage('Unable to parse error response.', 'Error');
            reject(error);
          }
        };

        reader.onerror = () => {
          this.showErrorMessage('Unable to read error response.', 'Error');
          reject(error);
        };

        reader.readAsText(error.error);
      })
    );
  }
}
